import * as $ from 'jquery';
import { IDisposable, IEventAggregator, disposable, serialDisposable, disposableSubscription, emptyDisposable } from 'common/barrels/core';
import { Observable, Subject, BehaviorSubject, AsyncSubject } from 'common/barrels/rx';
import { Request } from 'common/request';
import * as path from 'shared/path';

import { ValidationItem } from 'shared/data/validation';
import { ConcurrencyCheckFailed } from 'shared/messages/concurrencycheckfailed';

import { IResourceProvider } from 'common/resourceprovider';

import { Busy } from 'runtime/events/busy';
import { NotBusy } from 'runtime/events/notbusy';
import { LookupItemRequested } from 'runtime/events/lookupitemrequested';
import { LookupItemsRequested } from 'runtime/events/lookupitemsrequested';

import { AssignRequested } from 'runtime/events/workitem/assignrequested';
import { ForceCancelClaimRequested } from 'runtime/events/workitem/forcecancelclaimrequested';
import { CancelClaimRequested } from 'runtime/events/workitem/cancelclaimrequested';
import { TransferRequested } from 'runtime/events/workitem/transferrequested';
import { DialogRequested } from 'runtime/events/dialogrequested';
import { RuntimeHostClosed } from 'runtime/events/runtimehostclosed';
import { WorkTraySelectAllRequested } from 'runtime/events/worktrayselectallrequested';
import { RuntimeInfoRequested } from 'runtime/events/runtimeinforequested';

import { WorkTrayData } from 'runtime/data/worktraydata';
import { WorkTrayView } from 'runtime/data/worktrayview';
import { WorkTrayPage } from 'runtime/data/worktraypage';
import { BulkActionTarget } from 'runtime/data/bulkactiontarget';
import { LookupItem } from 'runtime/data/lookupitem';
import { WorkItemInfo } from 'runtime/data/workiteminfo';
import { UserInfo } from 'runtime/data/userinfo';
import { HistoryInfo } from 'runtime/data/historyInfo';
import { SelectAllPreference } from 'runtime/data/selectallpreference';

import { ViewScreen, IViewLoadPageArgs, ViewResultType, PageChangeSource, ViewScreenType } from './viewscreen';

import { SystemError } from 'common/exceptions/systemerror';
import { AccessDenied } from 'common/exceptions/accessdenied';
import { WorkItemNotAvailableForClaim } from 'runtime/messages/workitemnotavailableforclaim';
import { WorkItemNotAvailableForTransfer } from 'runtime/messages/workitemnotavailablefortransfer';
import { WorkItemNotAvailableForAssignment } from 'runtime/messages/workitemnotavailableforassignment';

import { IDialog } from 'runtime/dialogs/dialog';
import { WorkItemChangedDialog, WorkItemChangedDialogMode } from 'runtime/dialogs/workitemchangeddialog';
import { WorkItemNotAvailableForClaimDialog } from 'runtime/dialogs/workitemnotavailableforclaimdialog';
import { SelectAllDialog } from 'runtime/dialogs/selectalldialog';
import { ValidationReportDialog } from 'runtime/dialogs/validationreportdialog';
import { TransferAssignDialog } from 'runtime/dialogs/transferassigndialog';
import { CancelClaimDialog } from 'runtime/dialogs/cancelclaimdialog';
import { ConfirmProcessDialog } from 'runtime/dialogs/confirmprocessdialog';

import { Control } from 'runtime/controls/control';
import { NumericBox } from 'runtime/controls/numericbox';

import { ErrorReport } from 'runtime/errorreport';

import * as rxtable from 'common/rxui/rxtable';
import * as rxui from 'common/rxui/core';
import { Map } from 'immutable';

import 'runtime/styles/screens/workflow.less';
import 'runtime/styles/screens/worktray.less';
import 'runtime/styles/screens/viewmenudialog.less';

import { Serializer } from 'common/serializer';
import { LookupPropertyString } from 'runtime/data/lookupproperty';
import { BulkActionInfo } from 'runtime/data/bulkactioninfo';

interface IWorkTrayLoadPageArgs extends IViewLoadPageArgs {
    bulkAction: string;
    returnAllBulkActionTargets?: boolean;
}

interface IWorkTrayAutoRefreshArgs {
    autoRefresh: boolean;
    autoRefreshSeconds: number;
}

var settingsSeparator = '#';
var defaultAutoRefresh = true;
var defaultAutoRefreshSeconds = 30;
var minimumAutoRefreshSeconds = 5;
var maximumAutoRefreshSeconds = 999;

export class WorkTray extends ViewScreen {

    private bulkActions: LookupItem[];

    // action related subjects
    private allBulkActionTargets: BulkActionTarget[];
    private markedBulkActionTargets: Map<string, BulkActionTarget>;

    // page load related subjects
    private autoRefresh: BehaviorSubject<boolean>;
    private autoRefreshSeconds: BehaviorSubject<number>;
    private pageRefresh: Subject<boolean>;

    private autoRefreshDisposable = serialDisposable(null);
    private itemMenuDisposable = serialDisposable(null);

    private workTrayDisposable: IDisposable;

    private explicitWorkItemList: string[];

    constructor(
        $hostElement: JQuery,
        resourcesObservable: Observable<IResourceProvider>,
        parentEventAggregator: IEventAggregator,
        serializer: Serializer,
        view: string,
        explicitWorkItemList: string[],
        csrfToken: () => string
    ) {
        super($hostElement, resourcesObservable, parentEventAggregator, ViewScreenType.WorkTray, serializer, csrfToken);

        // action related subjects
        this.allBulkActionTargets = [];
        this.markedBulkActionTargets = Map<string, BulkActionTarget>();
        // page load related subjects
        this.autoRefresh = new BehaviorSubject(defaultAutoRefresh);
        this.autoRefreshSeconds = new BehaviorSubject(defaultAutoRefreshSeconds);
        this.pageRefresh = new Subject<boolean>();
        this.explicitWorkItemList = explicitWorkItemList;

        var asyncSubject = new AsyncSubject<WorkTrayData>();
        var asyncSubjectSubscription: IDisposable = null;
        asyncSubjectSubscription = disposableSubscription(Observable.zip(this.viewAvailable, asyncSubject, (v1, v2) => v2)
            .subscribe(value => this.initializeWorkTray(value, disposableSubscription(this.autoRefreshDisposable, this.itemMenuDisposable, asyncSubject, asyncSubjectSubscription))));

        const request = Request.create(this.serializer, path.api(`worktray/${view}`))
            .handle(WorkTrayData, value => {
                asyncSubject.next(value);
                asyncSubject.complete();
            })
            //.handle(null, value => asyncSubject.onError(value))
            .handle(AccessDenied, () => this.exception('Access Denied'))
            .handle(SystemError, t => this.systemError(t))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(errorThrown, textStatus))
            .final(() => this.eventAggregator.publish(new NotBusy()));

        if (explicitWorkItemList.length > 0) {
            request.args(() => explicitWorkItemList)
            request.post(csrfToken());
        } else {
            request.get();
        }
    }

    dispose(): void {
        this.workTrayDisposable.dispose();
    }

    remove(): void {
        this.$element.remove();
        this.dispose();
    }

    private initializeWorkTray(workTrayData: WorkTrayData, constructorDisposable: IDisposable): void {
        var initialAutoRefresh = defaultAutoRefresh;
        var initialAutoRefreshSeconds = defaultAutoRefreshSeconds;
        var initialPageSize = this.defaultPageSize;

        if (workTrayData.userSettings !== undefined && workTrayData.userSettings !== null && $.trim(workTrayData.userSettings) !== '') {
            var s = workTrayData.userSettings.split(settingsSeparator);

            initialAutoRefresh = (s[0] === 'True');
            this.autoRefresh.next(initialAutoRefresh);

            initialAutoRefreshSeconds = Number(s[1]);
            this.autoRefreshSeconds.next(initialAutoRefreshSeconds);

            if (s.length > 2) {
                initialPageSize = Number(s[2]);
                this.pageSize.next(initialPageSize);
            }
        }

        this.viewId.next(workTrayData.view.id);
        this.view.next(workTrayData.view);
        this.page.next(workTrayData.initialPage);

        var bulkActions = workTrayData.bulkActions;
        bulkActions.unshift(new BulkActionInfo(this.bulkActionNone, ''));
        this.bulkActions = bulkActions.map(ba => {
            let property = new LookupPropertyString();
            property.name = 'Description';
            property.value = this.resources.getBulkActionString(`Description.${ba.name}`) || ba.description;
            return new LookupItem(ba.name, this.resources.getBulkActionString(ba.name) || ba.name, [property], null, true);
        }).sort((a, b) => a.displayValue.localeCompare(b.displayValue));

        var pageSizeControl = <NumericBox>Control.instance($('.wf-page-size .numericbox')[0]);
        pageSizeControl['content.Value'](0);
        pageSizeControl['content.Value'](initialPageSize);
        pageSizeControl.setAdditionalValidator((raw, dec) => raw !== null && dec.greaterThan(0));

        this.buildWorkTrayContainer(this.$element, this.resources, initialAutoRefresh, initialAutoRefreshSeconds);

        Control.instance($('.wf-worktray-auto-refresh-seconds .numericbox')[0])['content.Value'](0);
        Control.instance($('.wf-worktray-auto-refresh-seconds .numericbox')[0])['content.Value'](initialAutoRefreshSeconds);

        this.paintViewHeader(workTrayData.view, ViewResultType.WorkTray);
        this.paintViewBodyVirtual(workTrayData.view, workTrayData.initialPage);

        this.initializeAutoRefresh({ autoRefresh: initialAutoRefresh, autoRefreshSeconds: initialAutoRefreshSeconds });

        this.workTrayDisposable = disposableSubscription(
            this.viewScreenDisposable,
            constructorDisposable,
            this.eventAggregator.queryRequest(LookupItemsRequested).filter(x => x.payload.fieldContext === 'bulkAction').subscribe(x => x.success(this.bulkActions)),
            this.eventAggregator.queryRequest(LookupItemRequested).filter(x => x.payload.fieldContext === 'bulkAction').subscribe(x => x.success(this.bulkActions.filter(ba => x.payload.keyValue === ba.keyValue)[0])),
            this.eventAggregator.query(TransferRequested).subscribe(x => this.itemTransfer(x)),
            this.eventAggregator.query(AssignRequested).subscribe(x => this.itemAssign(x)),
            this.eventAggregator.query(CancelClaimRequested).subscribe(x => this.itemCancelClaim(x)),
            this.eventAggregator.query(ForceCancelClaimRequested).subscribe(x => this.itemForceCancelClaim(x)),
            this.eventAggregator.query(WorkTraySelectAllRequested).subscribe(x => this.bulkSelectAll(x.selectAllPreference, x.completedAction)),

            this.eventAggregator.query(RuntimeHostClosed).subscribe(x => {
                var autoRefresh = this.autoRefresh.getValue();

                // auto refresh will requery the current page, but if disabled we need to manually update the current item
                if (!autoRefresh && x.workItemInfo !== undefined && x.workItemInfo !== null) {
                    this.updateItem(x.workItemInfo);
                }

                // re-initialize the auto refresh after disabling it on item edit/view
                this.resumeBackgroundProcessing();
            }),

            this.subscribeToItemActions(),
            this.subscribeToActions(),
            this.subscribeToPageLoadActions(),
            this.subscribeToSubjects()
        );

        // this must be called after subscribing to the LookupItem* events
        Control.instance($('.wf-worktray-bulk-actions .dropdownlist')[0])['content.SelectedValue'](this.bulkActionNone);

        this.viewInitialized = true;
    }

    private buildWorkTrayContainer($element: JQuery, resources: IResourceProvider, autoRefresh: boolean, refreshInterval: number): void {
        $('.wf-info-left', $element).append([
            $('<label />').addClass('wf-worktray-bulk-actions-label').text(resources.getCoreString('XamlBulkAction')),
            $('<div />').addClass('wf-worktray-bulk-actions').append($('<div />').dropDownList(this.eventAggregator, resources, 'bulkAction')),
            $('<div />').addClass('wf-worktray-bulk-action-process').append($('<div />').activityButton(this.eventAggregator, resources, { contentText: resources.getCoreString('XamlProcess'), isElementEnabled: false })),
            $('<div />').addClass('wf-worktray-bulk-action-selectall').append($('<div />').activityButton(this.eventAggregator, resources, { contentText: resources.getCoreString('XamlSelectAll'), isElementVisible: false })),
            $('<label />').addClass('wf-worktray-bulk-action-description') // description to be filled in subscription
        ]);

        var $autoRefreshInterval = $('<div />');

        $('.wf-info-right', $element).append([
            $('<label />').addClass('wf-worktray-auto-refresh-label').append([
                resources.getCoreString('XamlAutoRefresh'),
                $('<input />').addClass('wf-worktray-auto-refresh').attr('type', 'checkbox').prop('checked', autoRefresh)
            ]),
            $('<label />').addClass('wf-worktray-auto-refresh-seconds-label').text(resources.getCoreString('XamlRefreshPeriodSeconds')),
            $('<div />').addClass('wf-worktray-auto-refresh-seconds').append($autoRefreshInterval),
            $('<i />').addClass('wf-worktray-refresh fa fa-refresh')
        ]);

        $autoRefreshInterval.numericBox(this.eventAggregator, resources, { contentTextAlignment: 'center', contentValue: refreshInterval });
    }

    private subscribeToItemActions(): IDisposable {
        var $table = $('.wf-view-table-inner > table', this.$element);

        return disposableSubscription(
            rxui.click($table).subscribe(mouseEvent => {
                var $target = $(mouseEvent.target);

                var $menuItem = $target.closest('.wf-item-menu-item');

                if ($menuItem === undefined || $menuItem === null || $menuItem.length === 0) {
                    return;
                }

                // this stops the menu closing in some circumstances when it otherwise would/should. This has been dealt with (somewhat inelegantly) by explicitly closing the menu
                // when expanding / collapsing history
                mouseEvent.stopPropagation();

                var action = $menuItem.data('itemAction');

                var $tr = $target.closest('tr');

                var workItemInfo: WorkItemInfo = $tr.data('key');

                switch (action) {

                case 'showHistory':
                    this.itemMenuClose();
                    this.showHistory($tr, workItemInfo.workflowInstanceId/*, workItemInfo.workItemId*/);
                    $menuItem.find('i').removeClass('fa-plus').addClass('fa-minus');
                    $menuItem.data('itemAction', 'hideHistory');
                    break;

                case 'hideHistory':
                    this.itemMenuClose();
                    $menuItem.data('itemAction', 'showHistory');
                    $menuItem.find('i').removeClass('fa-minus').addClass('fa-plus');
                    var $historyTr = $tr.next('.wf-item-history');
                    $historyTr.next('.wf-item-history').remove();
                    $historyTr.remove();
                    break;

                case 'edit':
                    this.openEdit(workItemInfo.workItemId);
                    break;

                case 'view':
                    this.openRead(workItemInfo.workItemId, this.view.getValue().viewDataPreference);
                    break;

                case 'showMenu':
                    var $menu = this.itemMenuBuild(workItemInfo); //workItemId);
                    $menu.appendTo(this.$element.closest('.runtime-application-container'));
                    $menu.position({ my: 'left top', at: 'middle bottom', of: $menuItem });

                    var itemMenuSubscriptions = rxui.click($menu).subscribe(subMouseEvent => {
                        subMouseEvent.stopPropagation();

                        var $subTarget = $(subMouseEvent.target);

                        var subAction = $subTarget.data('itemAction');

                        // now that all related data has been accessed we can close/dispose the menu
                        this.itemMenuClose();

                        switch (subAction) {
                        case 'edit':
                            this.openEdit(workItemInfo.workItemId);
                            break;

                        case 'view':
                            this.openRead(workItemInfo.workItemId, this.view.getValue().viewDataPreference);
                            break;

                        case 'transfer':
                            Request.create(this.serializer, path.api(`worktray/items/${workItemInfo.workItemId}/transferTargets`))
                                .before(() => this.eventAggregator.publish(new Busy()))
                                .handleArray(UserInfo, targets => this.eventAggregator.publish(new DialogRequested(new TransferAssignDialog(true, workItemInfo, targets, this.eventAggregator, this.resources))))
                                .handle(WorkItemNotAvailableForTransfer, () => alert('need a error handle for \'messages.WorkItemNotAvailableForTransfer\''))
                                .error((jqXhr, textStatus, errorThrown) => this.httpError(null, textStatus))
                                .final(() => this.eventAggregator.publish(new NotBusy()))
                                .get();
                            break;

                        case 'assign':
                                Request.create(this.serializer, path.api(`worktray/items/${workItemInfo.workItemId}/assignTargets`))
                                .before(() => this.eventAggregator.publish(new Busy()))
                                .handleArray(UserInfo, targets => this.eventAggregator.publish(new DialogRequested(new TransferAssignDialog(false, workItemInfo, targets, this.eventAggregator, this.resources))))
                                .handle(WorkItemNotAvailableForAssignment, () => alert('need a error handle for \'messages.WorkItemNotAvailableForAssignment\''))
                                .error((jqXhr, textStatus, errorThrown) => this.httpError(null, textStatus))
                                .final(() => this.eventAggregator.publish(new NotBusy()))
                                .get();
                            break;

                        case 'cancelClaim':
                        case 'forceCancelClaim':
                            this.eventAggregator.publish(new DialogRequested(new CancelClaimDialog((subAction === 'forceCancelClaim'), this.eventAggregator, workItemInfo, this.resources)));
                            break;

                        case 'claim':
                            this.itemClaim(workItemInfo);
                            break;
                        }
                    });

                    this.itemMenuDisposable.set(disposableSubscription(
                        itemMenuSubscriptions,
                        rxui.clickOutside($menu).merge(rxui.scrollOutside($menu)).merge(rxui.windowResized()).subscribe(() => this.itemMenuClose()),
                        disposable(() => {
                            $menu.remove();
                            $menuItem.data('itemAction', 'showMenu');
                            $menuItem.find('i').removeClass('fa-caret-up').addClass('fa-caret-down');
                        })
                    ));

                    $menuItem.find('i').removeClass('fa-caret-down').addClass('fa-caret-up');
                    $menuItem.data('itemAction', 'hideMenu');
                    break;

                case 'hideMenu':
                    this.itemMenuClose();
                    break;
                }
            }),
            rxui.change($table).subscribe(changeEvent => {
                var $input = $(changeEvent.target);

                changeEvent.stopPropagation();

                var $tr = $input.closest('tr');
                var workItemInfo: WorkItemInfo = $tr.data('key');

                workItemInfo.markedForProcessing = $input.prop('checked');
            })
       );
    }

    private subscribeToActions(): IDisposable {
        var $bulkAction = this.$element.find('.wf-worktray-bulk-actions .dropdownlist');

        return disposableSubscription(
            rxui.on<string>($bulkAction, 'content.SelectedValue').subscribe(value => this.bulkAction.next(value)),

            rxui.click($('.wf-worktray-bulk-action-process .activitybutton', this.$element))
                .subscribe(
                    () => {
                        this.updating();

                        var bulkAction = this.bulkAction.getValue();

                        this.workItemsBulkStateToBulkMap();

                        var args = { bulkAction: bulkAction, targets: this.markedBulkActionTargets.valueSeq().toArray() };

                        if (args.targets.length === 0) {
                            var errorReport = new ErrorReport(this.resources);
                            errorReport.addError({ message: this.resources.getCoreString('ErrAtLeastOneItemMustBeSelected') });
                            this.notUpdating();
                            return;
                        }

                        // Show confirmation dialog with selected item count
                        var confirmDialog = new ConfirmProcessDialog(
                            this.resources,
                            args.targets.length,
                            () => {
                                // Confirm action - proceed with processing
                                this.updating();
                                var handleValidationReport = (x: ValidationItem[]): void => {
                                    this.eventAggregator.publish(new DialogRequested(new ValidationReportDialog(x, this.resources)));
                                    Control.instance($('.wf-worktray-bulk-actions>div', this.$element)[0])['content.SelectedValue'](this.bulkActionNone);
                                    this.bulkAction.next(this.bulkActionNone);
                                };

                                Request.create(this.serializer, path.api('worktray/bulkaction'))
                                    .args(() => args)
                                    .handleArray(ValidationItem, handleValidationReport)
                                    .handle(null, () => this.bulkAction.next(this.bulkActionNone))
                                    .final(() => this.notUpdating())
                                    .post(this.csrfToken());
                            },
                            () => {
                                // Cancel action - abort processing
                                this.notUpdating();
                            }
                        );
                        
                        this.eventAggregator.publish(new DialogRequested(confirmDialog));
                        this.notUpdating();
                    }),

            rxui.click($('.wf-worktray-bulk-action-selectall .activitybutton', this.$element))
                .subscribe(() => this.bulkSelectAll(this.view.getValue().selectAllPreference, () => { }))
        );
    }

    private subscribeToPageLoadActions(): IDisposable {
        var chkAutoRefresh = $('.wf-worktray-auto-refresh', this.$element);
        var txtAutoRefreshSeconds = $('.wf-worktray-auto-refresh-seconds .numericbox', this.$element);
        var btnRefresh = $('.wf-worktray-refresh', this.$element);

        return disposableSubscription(
            rxui.change(chkAutoRefresh).subscribe(() => this.autoRefresh.next(chkAutoRefresh.prop('checked'))),
            rxui.on<number>(txtAutoRefreshSeconds, 'content.Value').subscribe(autoRefreshSeconds => {
                if (autoRefreshSeconds < minimumAutoRefreshSeconds) {
                    Control.instance(txtAutoRefreshSeconds[0])['set Value'](minimumAutoRefreshSeconds);
                } else if (autoRefreshSeconds > maximumAutoRefreshSeconds) {
                    Control.instance(txtAutoRefreshSeconds[0])['set Value'](maximumAutoRefreshSeconds);
                } else {
                    this.autoRefreshSeconds.next(autoRefreshSeconds);
                }
            }),
            rxui.click(btnRefresh).subscribe(() => this.pageRefresh.next(true))
        );
    }

    private subscribeToSubjects(): IDisposable {
        var bulkActionSubscription = this.bulkAction
            .distinctUntilChanged()
            .skipWhile(() => !this.viewInitialized)
            .subscribe(bulkAction => {
                this.updating();

                var view = this.view.getValue();

                // call paintViewHeader to handle display of the checkbox column, determined from bulk action
                this.paintViewHeader(view, (bulkAction === this.bulkActionNone) ? ViewResultType.WorkTray : ViewResultType.WorkTrayBulkAction);

                Control.instance($('.wf-worktray-bulk-action-process .activitybutton', this.$element)[0]).isElementEnabled(bulkAction !== this.bulkActionNone);
                Control.instance($('.wf-worktray-bulk-action-selectall .activitybutton', this.$element)[0]).isElementVisible(bulkAction !== this.bulkActionNone);

                const description = this.getBulkActionDescription(bulkAction);
                const $descriptionLabel = $('.wf-worktray-bulk-action-description', this.$element)[0];
                $descriptionLabel.textContent = description;
                $descriptionLabel.title = description;

                this.markedBulkActionTargets = this.markedBulkActionTargets.clear();

                var args: IWorkTrayLoadPageArgs = {
                    viewId: view.id,
                    pageSize: this.pageSize.getValue(),
                    pageIndex: this.firstPageIndex,
                    sortDescriptions: this.sortDescriptions.getValue(),
                    bulkAction: bulkAction
                };

                // reload the page to include all bulk actions, determined from bulk action
                this.loadPage(args, () => this.notUpdating());
            });

        var autoRefreshSource: Observable<IWorkTrayAutoRefreshArgs> = this.autoRefresh.map(value => ({ autoRefresh: value, autoRefreshSeconds: this.autoRefreshSeconds.getValue() }));

        var autoRefreshSecondsSource: Observable<IWorkTrayAutoRefreshArgs> = this.autoRefreshSeconds
            .distinctUntilChanged()
            .map(value => ({ autoRefresh: this.autoRefresh.getValue(), autoRefreshSeconds: value }));

        //var autoRefreshMergeSubscription = Observable.merge([autoRefreshSource, autoRefreshSecondsSource])
        var autoRefreshMergeSubscription = autoRefreshSource.merge(autoRefreshSecondsSource)
            .skipWhile(() => !this.viewInitialized)
            .subscribe(value => this.initializeAutoRefresh(value));

        // subscribe to user setting observables
        var userSettingsSubscription = Observable.combineLatest(this.pageSize, this.autoRefresh, this.autoRefreshSeconds, (v1: number, v2: boolean, v3: number) => ({ pageSize: v1, autoRefresh: v2, autoRefreshSeconds: v3 }))
            .skipWhile(() => !this.viewInitialized)
            .subscribe(value => this.setUserSettings(value));

        var pageRefreshSource: Observable<PageChangeSource> = this.pageRefresh
            .map(() => new PageChangeSource(null, null));

        var pageChangeSubscription = Observable.merge(this.pageSizeSource, this.pageIndexSource, pageRefreshSource)
            .skipWhile(() => !this.viewInitialized)
            .filter(() => !this.viewUpdating)
            .subscribe(
                value => {
                    this.updating(this.$viewTable);

                    var args: IWorkTrayLoadPageArgs = {
                        viewId: this.view.getValue().id,
                        pageSize: value.pageSize || this.pageSize.getValue(),
                        pageIndex: value.pageIndex || this.pageIndex.getValue(),
                        sortDescriptions: this.sortDescriptions.getValue(),
                        bulkAction: this.bulkAction.getValue()
                    };

                    this.loadPage(args, () => this.notUpdating(this.$viewTable));
                },
                // this should never execute as the subscriptions are to behavior subjects of primitive types
                (exception: any) => {
                    console.log(JSON.stringify(exception));
                    alert('ERROR.  Check console');
                }
            );

        return disposableSubscription(
            bulkActionSubscription,
            autoRefreshMergeSubscription,
            userSettingsSubscription,
            pageChangeSubscription
        );
    }

    private initializeAutoRefresh(x: IWorkTrayAutoRefreshArgs) {
        // when auto refresh is enabled, schedule the timer to run a refresh immediately and then on the schedule
        this.autoRefreshDisposable.set(disposableSubscription(x.autoRefresh
            ? Observable.timer(0, x.autoRefreshSeconds * 1000).subscribe(() => this.pageRefresh.next(true))
            : emptyDisposable
        ));
    }

    private setUserSettings(value: { pageSize: number; autoRefresh: boolean; autoRefreshSeconds: number }): void {
        var settings: string[] = [
            value.autoRefresh ? 'True' : 'False',
            value.autoRefreshSeconds.toString()
        ];
        if (value.pageSize !== this.defaultPageSize) {
            settings.push(value.pageSize.toString());
        }

        Request.create(this.serializer, path.api('worktray/usersettings'))
            .args(() => settings.join(settingsSeparator))
            .handle(null, () => { })
            .handle(SystemError, () => this.systemError('WorkTray.setUserSettings: Internal System Error'))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(`WorkTray.setUserSettings: ${errorThrown}`, textStatus))
            .post(this.csrfToken());
    }

    private loadPage(args: IWorkTrayLoadPageArgs, final: () => void, action: () => void = null): void {
        var requestArgs: any = {
            pageSize: args.pageSize,
            pageIndex: args.pageIndex
        };
        if (args.sortDescriptions !== undefined && args.sortDescriptions !== null && args.sortDescriptions.length > 0) {
            requestArgs.sortDescriptions = args.sortDescriptions;
        }
        if (args.bulkAction !== undefined && args.bulkAction !== null && args.bulkAction !== this.bulkActionNone) {
            requestArgs.bulkAction = args.bulkAction;
        }
        if (args.returnAllBulkActionTargets !== undefined && args.returnAllBulkActionTargets !== null && args.returnAllBulkActionTargets) {
            requestArgs.returnAllBulkActionTargets = true;
        }

        if (this.explicitWorkItemList && this.explicitWorkItemList.length > 0) {
            requestArgs.explicitWorkItemList = this.explicitWorkItemList;
        }

        Request.create(this.serializer, path.api(`worktray/page/${args.viewId}`))
            .args(() => requestArgs)
            .handle(WorkTrayPage,
                page => {
                    this.page.next(page);

                    if (action !== null) {
                        action();
                    }
                })
            .handle(SystemError, () => this.systemError('Error in loadPage: Interanl System Error'))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(`Error in loadPage: ${errorThrown}`, textStatus))
            .final(() => final())
            .post(this.csrfToken());
    }

    private showHistory($target: JQuery, workflowInstanceId: string): void {
        var renderHistory = (historyItems: HistoryInfo[]) => {
            var columnMetadata = [
                new rxtable.ColumnInfo('summary', this.resources.getCoreString('XamlSummary'), 0, 'auto'),
                new rxtable.ColumnInfo('stepTitle', this.resources.getCoreString('XamlStep'), 250, 'pixel'),
                new rxtable.ColumnInfo('dateTime', this.resources.getCoreString('XamlTime'), 250, 'pixel'),
                new rxtable.ColumnInfo('userDisplayName', this.resources.getCoreString('XamlUser'), 250, 'pixel')
            ];

            var items = historyItems.map(h => ({ active: true, summary: h.summary(this.resources), stepTitle: h.stepTitle, dateTime: this.toLongDateTimeFormat(h.dateTime), userDisplayName: h.userDisplayName }));

            this.insertHistoryTable($target, columnMetadata, items, false);
        };

        Request.create(this.serializer, path.api(`worktray/history/${workflowInstanceId}`))
            .before(() => this.updating(this.$viewTable))
            .handleArray(HistoryInfo, history => renderHistory(history))
            .handle(null, () => renderHistory([]))
            .handle(SystemError, () => this.systemError('System Error : showHistory'))
            .error((jqXhr, textStatus, errorThrown) => this.httpError(`Http Error : showHistory : ${errorThrown}`, textStatus))
            .final(() => this.notUpdating(this.$viewTable))
            .post(this.csrfToken());
    }

    private bulkSelectAll(preference: string, completedAction: () => void) {
        switch (preference) {
            case SelectAllPreference.all:
                this.updating();

                var action = (): void => {
                    this.markedBulkActionTargets = this.markedBulkActionTargets.clear();
                    this.allBulkActionTargets.forEach(x => this.markedBulkActionTargets = this.markedBulkActionTargets.set(x.workItemId, x));
                    this.bulkMapToWorkItemBulkState();

                    completedAction();
                };

                var args: IWorkTrayLoadPageArgs = {
                    viewId: this.view.getValue().id,
                    pageSize: this.pageSize.getValue(),
                    pageIndex: this.pageIndex.getValue(),
                    sortDescriptions: this.sortDescriptions.getValue(),
                    bulkAction: this.bulkAction.getValue(),
                    returnAllBulkActionTargets: true
                };
                this.loadPage(args, () => this.notUpdating(), action);
                break;
            case SelectAllPreference.allCurrentPage:
                var allCurrentPageOnNext = (page: WorkTrayPage): void => {
                    page.items.forEach(x => x.markedForProcessing = true);
                    $('.wf-item-process', this.$element).prop('checked', true);

                    completedAction();
                };
                allCurrentPageOnNext(<WorkTrayPage>this.page.getValue());
                break;
            case SelectAllPreference.prompt:
            default:
                this.eventAggregator.publish(new DialogRequested(new SelectAllDialog(this.eventAggregator, this.resources)));
                break;
        }
    }

    private itemMenuBuild(workItemInfo: WorkItemInfo): JQuery {
        var $menuItems = $('<ul />').addClass('ui-menu');

        if (workItemInfo.canEdit) {
            $('<li />')
                .addClass('wf-item-menu-item')
                .addClass('ui-menu-item')
                .data('itemAction', 'edit')
                .text(this.resources.getCoreString('XamlEdit'))
                .appendTo($menuItems);
        }

        if (workItemInfo.canView) {
            $('<li />')
                .addClass('wf-item-menu-item')
                .addClass('ui-menu-item')
                .data('itemAction', 'view')
                .text(this.resources.getCoreString('XamlView'))
                .appendTo($menuItems);
        }

        if (workItemInfo.canTransfer) {
            $('<li />')
                .addClass('wf-item-menu-item')
                .addClass('ui-menu-item')
                .data('itemAction', 'transfer')
                .text(this.resources.getCoreString('XamlTransfer'))
                .appendTo($menuItems);
        }

        if (workItemInfo.canAssign) {
            $('<li />')
                .addClass('wf-item-menu-item')
                .addClass('ui-menu-item')
                .data('itemAction', 'assign')
                .text(this.resources.getCoreString('XamlAssign'))
                .appendTo($menuItems);
        }

        if (workItemInfo.canEdit && workItemInfo.state === 'available') {
            $('<li />')
                .addClass('wf-item-menu-item')
                .addClass('ui-menu-item')
                .data('itemAction', 'claim')
                .text(this.resources.getCoreString('XamlClaim'))
                .appendTo($menuItems);
        }

        if (workItemInfo.canCancel) {
            $('<li />')
                .addClass('wf-item-menu-item')
                .addClass('ui-menu-item')
                .data('itemAction', 'cancelClaim')
                .text(this.resources.getCoreString('XamlCancelMyClaim'))
                .appendTo($menuItems);
        }

        if (workItemInfo.canForceCancel) {
            $('<li />')
                .addClass('wf-item-menu-item')
                .addClass('ui-menu-item')
                .data('itemAction', 'forceCancelClaim')
                .text(this.resources.getCoreString('XamlForceCancelClaim'))
                .appendTo($menuItems);
        }

        return $('<div />').addClass('wf-item-menu').append($menuItems);
    }

    private itemMenuClose(): void {
        this.itemMenuDisposable.set(null);
    }

    private itemTransfer(request: TransferRequested): void {
        this.executeItemServerRequest(
            request.workItemInfo.workItemId,
            'transfer',
            () => ({ workItemVersionId: request.workItemInfo.versionId, targetUserId: request.targetUserId }),
            () => request.completionAction());
    }

    private itemAssign(request: AssignRequested): void {
        this.executeItemServerRequest(
            request.workItemInfo.workItemId,
            'assign',
            () => ({ workItemVersionId: request.workItemInfo.versionId, targetUserId: request.targetUserId }),
            () => request.completionAction());
    }

    private itemCancelClaim(request: CancelClaimRequested): void {
        this.executeItemServerRequest(
            request.workItemInfo.workItemId,
            'cancel',
            () => ({ workItemVersionId: request.workItemInfo.versionId }),
            () => request.completionAction());
    }

    private itemForceCancelClaim(request: ForceCancelClaimRequested): void {
        this.executeItemServerRequest(
            request.workItemInfo.workItemId,
            'forcecancel',
            () => ({ workItemVersionId: request.workItemInfo.versionId }),
            () => request.completionAction());
    }

    private itemClaim(workItemInfo: WorkItemInfo): void {
        this.executeItemServerRequest(
            workItemInfo.workItemId,
            'claim');
    }

    private executeItemServerRequest(workItemId: string, action: string, args: () => any = null, final: () => void = null): void {
        this.eventAggregator.publish(new Busy());

        var onCompleted = () => {
            if (final !== undefined && final !== null) {
                final();
            }
            this.eventAggregator.publish(new NotBusy());
        };

        var onError = (jqXhr: JQueryXHR, textStatus: string, errorThrown: string): void => this.httpError(`Http Error : item.${action} : ${errorThrown}`, textStatus);

        var refreshWorkItem = (dialog: IDialog): void => {
            Request.create(this.serializer, path.api(`worktray/items/${workItemId}`))
                .handle(WorkItemInfo, x => {
                    this.updateItem(x);
                    this.eventAggregator.publish(new DialogRequested(dialog));
                })
                .handle(SystemError, t => this.systemError(t))
                .error(onError)
                // todo: this is not right. It can't do this because it's double-completing the original final action (which now error in the dialog subsystem)
                // I'm now concerned about remaining busy for the appropriate period without any gaps
                // .final(onCompleted)
                .get();
        };

        new Request(this.serializer, path.api(`worktray/items/${workItemId}/${action}`))
            .args(args)
            .error(onError)
            .handle(SystemError, t => this.systemError(t))
            .handle(ConcurrencyCheckFailed,() => refreshWorkItem(new WorkItemChangedDialog(WorkItemChangedDialogMode.DialogSpecific, null, this.resources, this.resources.getCoreString('ErrFailedToPerformAction'))))
            .handle(WorkItemNotAvailableForClaim, msg => refreshWorkItem(new WorkItemNotAvailableForClaimDialog(msg.reason, msg.workItemInfo.claimUserInfo, this.resources)))
            .handle(WorkItemInfo, x => this.updateItem(x))
            .final(onCompleted)
            .post(this.csrfToken());
    }

    private updateItem(workItemInfo: WorkItemInfo): void {
        var view = this.view.getValue();
        var page = <WorkTrayPage>this.page.getValue();

        // update the local data with the returned version
        var itemIndex = page.items.findIndex(x => x.workItemId === workItemInfo.workItemId);
        page.items[itemIndex] = workItemInfo;

        const runtimeInfo = new RuntimeInfoRequested();
        this.eventAggregator.publish(runtimeInfo);

        var item = this.generateLookupItem(ViewResultType.WorkTray, view.displayHistory, view.displayActionMenu, workItemInfo, this.columnMetadata.map(c => c.name), runtimeInfo.value.userId);
        var $newTr = $(rxtable.optimizedRowConstruction(item, this.columnMetadata, x => x.keyValue));

        var $table = $('.wf-view-table-inner > table', this.$element);
        var $tbody = $table.children('tbody');
        var $oldTr = $tbody.children(`:nth-child(${(itemIndex + 1)})`);

        $oldTr.replaceWith($newTr);

        rxtable.applyAlternatingRowStyles($table);
    }

    protected pauseBackgroundProcessing(): void {
        this.autoRefreshDisposable.set(null);
    }

    protected resumeBackgroundProcessing(): void {
        this.initializeAutoRefresh({ autoRefresh: this.autoRefresh.getValue(), autoRefreshSeconds: this.autoRefreshSeconds.getValue() });
    }

    protected onViewChanged(view: WorkTrayView, final: () => void): void {
        this.paintViewHeader(view, (this.bulkAction.getValue() === this.bulkActionNone) ? ViewResultType.WorkTray : ViewResultType.WorkTrayBulkAction);

        var workTrayArgs: IWorkTrayLoadPageArgs = {
            viewId: view.id,
            pageSize: this.pageSize.getValue(),
            pageIndex: this.firstPageIndex,
            sortDescriptions: this.sortDescriptions.getValue(),
            bulkAction: this.bulkAction.getValue()
        };

        this.loadPage(workTrayArgs, final);
    }

    protected reloadPage(args: IViewLoadPageArgs, final: () => void): void {
        var workTrayArgs: IWorkTrayLoadPageArgs = {
            viewId: args.viewId,
            pageSize: args.pageSize,
            pageIndex: args.pageIndex,
            sortDescriptions: args.sortDescriptions,
            bulkAction: this.bulkAction.getValue()
        };

        this.loadPage(workTrayArgs, final);
    }

    private workItemInfos = () => $('.wf-view-table-inner > table > tbody > tr', this.$element).toArray()
        .map(x => $(x))
        .map($tr => ({ $input: $('.wf-item-process', $tr), item: <WorkItemInfo>$tr.data('key') }))
        .filter(x => x.$input !== undefined && x.$input !== null && x.$input.length > 0);

    // for the existing page of work items, update the list of previously marked items
    private workItemsBulkStateToBulkMap = (): void => this.workItemInfos().forEach(itemInfo => {
        var item = itemInfo.item;
        if (this.markedBulkActionTargets.has(item.workItemId)) {
            this.markedBulkActionTargets = this.markedBulkActionTargets.remove(item.workItemId);
        }
        if (item.markedForProcessing) {
            this.markedBulkActionTargets = this.markedBulkActionTargets.set(item.workItemId, new BulkActionTarget(item.workItemId, item.versionId));
        }
    });

    // for the new page of work items, if any are contained in the list of previously marked items then mark them for processing
    private bulkMapToWorkItemBulkState = (): void => {
        this.workItemInfos().forEach(itemInfo => {
            var item = itemInfo.item;
            item.markedForProcessing = this.markedBulkActionTargets.has(item.workItemId);
            itemInfo.$input.prop('checked', item.markedForProcessing);
        });
    };

    protected paintViewBodyVirtual(view: WorkTrayView, page: WorkTrayPage): void {
        this.workItemsBulkStateToBulkMap();

        this.paintViewBody(view.displayHistory, view.displayActionMenu, page);

        this.bulkMapToWorkItemBulkState();

        this.allBulkActionTargets = page.allBulkActionTargets;
    }

    private getBulkActionDescription(bulkAction: string): string {
        let ba = this.bulkActions.find(x => x.keyValue === bulkAction);
        return ba.properties.filter(x => x instanceof LookupPropertyString)
            .map(x => <LookupPropertyString>x)
            .find(x => x.name === 'Description')?.value || '';
    }
}